import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCode, normalizeRedemptionCode } from '../format';

interface Props {
  code: string;
  /** Size and colour of the code: each place keeps its own. */
  codeClassName: string;
  /** Margins of the row. */
  className?: string;
  /** The full-screen QR is deliberately not themed: black on white. */
  tone?: 'ink' | 'black';
}

type Feedback = 'idle' | 'copied' | 'selected';

const FEEDBACK_MS = 2000;

/**
 * A card or coupon code with a « Copier » button (SC-1642): the coupon hint says
 * to type the code at online checkout, and retyping 8–10 characters on a phone
 * is where it went wrong. What lands in the clipboard is the bare code
 * (`K7M2QX9PAB`) — what the checkout field and the hub's `normalize_code` keep
 * anyway. No clipboard API (http page, old browser) or a refused write: the code
 * is selected instead, so a long-press copies it — never a « Copié » that is not.
 */
export default function CopyableCode({ code, codeClassName, className = '', tone = 'ink' }: Props) {
  const { t } = useTranslation();
  const codeRef = useRef<HTMLParagraphElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [feedback, setFeedback] = useState<Feedback>('idle');

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const show = (next: Feedback) => {
    if (timer.current) clearTimeout(timer.current);
    setFeedback(next);
    timer.current = setTimeout(() => setFeedback('idle'), FEEDBACK_MS);
  };

  const selectCode = () => {
    try {
      const node = codeRef.current;
      const selection = window.getSelection?.();
      if (!node || !selection) return;
      selection.selectAllChildren(node);
      show('selected');
    } catch {
      /* nothing left to try: the code stays on screen */
    }
  };

  const copy = () => {
    let pending: Promise<void> | undefined;
    try {
      pending = navigator.clipboard?.writeText(normalizeRedemptionCode(code));
    } catch {
      pending = undefined;
    }
    if (!pending) {
      selectCode();
      return;
    }
    pending.then(() => show('copied'), selectCode);
  };

  const buttonTone =
    tone === 'black' ? 'border-black bg-white text-black' : 'border-ink bg-paper text-ink';
  const feedbackTone = tone === 'black' ? 'text-black/70' : 'text-ink-mute';

  return (
    <div className={`flex flex-wrap items-center justify-center gap-x-3 gap-y-1 ${className}`}>
      <p
        ref={codeRef}
        dir="ltr"
        className={`loyalty-code m-0 font-mono font-bold tracking-widest ${codeClassName}`}
      >
        {formatCode(code)}
      </p>
      <button
        type="button"
        onClick={copy}
        data-testid="loyalty-copy-code"
        className={`min-h-[44px] flex-none rounded-btn-sm border-2 border-solid px-3 font-display text-sm ${buttonTone}`}
      >
        {t('loyalty.copy.button')}
      </button>
      {/* Its own line, height kept: the feedback appearing moves nothing. A live
          region kept in the DOM so it is read when it fills — not a second
          role="status" next to the page's own. */}
      <p
        aria-live="polite"
        data-testid="loyalty-copy-feedback"
        className={`m-0 min-h-[1rem] basis-full text-center font-mono text-xs ${feedbackTone}`}
      >
        {feedback === 'copied'
          ? `✓ ${t('loyalty.copy.done')}`
          : feedback === 'selected'
            ? t('loyalty.copy.selected')
            : ''}
      </p>
    </div>
  );
}
